/**
 * RES-43 — Alertas de stock bajo del dock agrupadas por local (épica D1).
 *
 * El dock junta dos fuentes: la lista periódica (stock del local seleccionado) y los `LOW_STOCK`
 * que llegan por WebSocket (pueden venir de otro local de la cadena). Se muestra una alerta por
 * local con los insumos en falta, en vez de una por insumo.
 */

/** Insumos que se nombran en la alerta; el resto se cuenta como "+N". */
export const MAX_NAMES_PER_GROUP = 3;

/**
 * Local de un evento de WebSocket: el del mensaje, el del payload o el seleccionado.
 * @param {Object} message - Mensaje `{ type, data, restaurantId? }`
 * @param {string} fallback - Local seleccionado
 * @returns {string} Local
 */
export function eventRestaurantId(message, fallback) {
  return (message && (message.restaurantId || message.data?.restaurantId)) || fallback || '';
}

/**
 * Agrupa las alertas de stock bajo por local.
 * @param {Object} params - Fuentes
 * @param {Array<{restaurantId: string, itemId: string, name: string}>} params.realtime - Eventos
 *   `LOW_STOCK` vigentes, del más nuevo al más viejo
 * @param {Array<{itemId: string, name: string}>} params.periodic - Stock bajo del local seleccionado
 * @param {string} params.selectedRestaurantId - Local seleccionado
 * @returns {Array<{restaurantId: string, names: string[], count: number, realtime: boolean}>}
 *   Un grupo por local: primero los que tienen eventos en tiempo real
 */
export function groupLowStockAlerts({ realtime = [], periodic = [], selectedRestaurantId = '' }) {
  const groups = new Map();
  const add = (restaurantId, itemId, name, isRealtime) => {
    const key = restaurantId || selectedRestaurantId || '';
    if (!groups.has(key)) {
      groups.set(key, { restaurantId: key, items: new Map(), realtime: false });
    }
    const group = groups.get(key);
    if (isRealtime) group.realtime = true;
    const itemKey = itemId || name;
    if (!group.items.has(itemKey)) group.items.set(itemKey, name || itemId);
  };

  realtime.forEach((entry) => add(entry.restaurantId, entry.itemId, entry.name, true));
  periodic.forEach((entry) => add(selectedRestaurantId, entry.itemId, entry.name, false));

  const list = [...groups.values()].map((group) => {
    const names = [...group.items.values()];
    return {
      restaurantId: group.restaurantId,
      names,
      count: names.length,
      realtime: group.realtime,
    };
  });
  // Estable: el orden de inserción ya pone primero lo más nuevo en tiempo real
  return [...list.filter((g) => g.realtime), ...list.filter((g) => !g.realtime)];
}

/**
 * Texto con los nombres de un grupo: "Harina, Aceite, Sal +2".
 * @param {string[]} names - Insumos
 * @param {number} [max] - Máximo de nombres
 * @returns {string} Texto
 */
export function lowStockNames(names, max = MAX_NAMES_PER_GROUP) {
  const shown = (names || []).slice(0, max).join(', ');
  const extra = (names || []).length - max;
  return extra > 0 ? `${shown} +${extra}` : shown;
}
